import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CommitteeMeetingForm, CommitteeMeetingCard } from "../components/CommitteeMeeting";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine } from "recharts";

const TABS = [
  ["trends", "Trends"],
  ["incident_reports", "Incidents & Complaints"],
  ["audit", "Audits"],
  ["improvement_plan", "Improvement Plans"],
  ["committee_note", "Committee Reviews"],
  ["caps", "Corrective Action Plans"],
  ["qm", "Meetings"],
];

const QM_MEASURE_DEFS = [
  ["evv", "EVV Compliance Rate"],
  ["billable_hours", "Total Billable Hours"],
  ["visits", "Completed Visits / Missed Visits"],
  ["incidents", "Incidents (near miss / critical / non-reportable / APS / preventable / interventions)"],
  ["complaints", "Complaints (number / resolved / referred to MCO / unresolved)"],
  ["task_entry", "Task Code Entry Rate"],
  ["task_compliance", "Task Code Compliance Rate"],
  ["overnight", "Overnight Check-in Compliance Rate"],
];
const QM_POLICIES = ["Personal Care Services", "Sexual Harassment", "Limited English Proficiency (LEP)",
  "Records Management", "Dress Code", "Social Security Verification", "OIG Inspection Lists",
  "Securing Electronic Records and Documents"];
const QM_NOTES = [["weekly_report_issues", "Weekly Report (New Hire & Employee Compliance) — non-compliance issues"],
  ["previous_actions", "Previous Meeting Actions/Outcomes"], ["other_topics", "Other Notable Topics or Projects"]];
const QM_STATUS_OPTS = [["improved", "Improved"], ["constant", "Remained Constant"], ["declined", "Declined"]];
const QM_REVIEW_OPTS = [["reviewed", "Reviewed"], ["updated", "Updated"], ["no_changes", "No Changes"]];
const INCIDENT_CATEGORIES = ["Fall", "Abuse / neglect / exploitation", "Medication error", "Serious injury", "Death",
  "Service interruption", "Unsafe environment", "Unplanned hospitalization", "Missed visit", "Other"];
const SERIOUS_CATEGORIES = INCIDENT_CATEGORIES.slice(0, 7);

const STATUS_CLS = {
  Open: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]",
  "In progress": "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  Complete: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
};

function EntryCard({ e, onStatus, onDelete }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`qi-entry-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-stone-900">{e.title}</p>
          <p className="mt-0.5 text-xs text-stone-400">
            {e.date || "no date"}
            {e.audit_type ? ` · ${e.audit_type}` : ""}
            {e.quarter ? ` · ${e.quarter}` : ""}
            {e.owner ? ` · owner: ${e.owner}` : ""}
            {e.due_date ? ` · due ${e.due_date}` : ""}
          </p>
        </div>
        <span className="flex items-center gap-2">
          {e.kind === "improvement_plan" && (
            <Select value={e.status || "Open"} onValueChange={(v) => onStatus(e, v)}>
              <SelectTrigger className={`h-7 w-32 border text-xs font-medium ${STATUS_CLS[e.status] || STATUS_CLS.Open}`} data-testid={`qi-status-${e.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Open", "In progress", "Complete"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button onClick={() => onDelete(e)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`qi-delete-${e.id}`} title="Delete">
            <Trash size={14} />
          </button>
        </span>
      </div>
      {(e.scope || e.description || e.summary) && <p className="mt-2 text-sm text-stone-600">{e.scope || e.description || e.summary}</p>}
      {e.findings && <p className="mt-1.5 text-sm text-stone-600"><span className="font-medium text-stone-700">Findings:</span> {e.findings}</p>}
      {e.result && <p className="mt-1 text-sm text-stone-600"><span className="font-medium text-stone-700">Result:</span> {e.result}</p>}
      {e.actions && <p className="mt-1.5 text-sm text-stone-600"><span className="font-medium text-stone-700">Actions:</span> {e.actions}</p>}
      {e.outcome && <p className="mt-1 text-sm text-stone-600"><span className="font-medium text-stone-700">Outcome:</span> {e.outcome}</p>}
      {e.attendees && <p className="mt-1.5 text-xs text-stone-500">Attendees: {e.attendees}</p>}
      {e.decisions && <p className="mt-1 text-sm text-stone-600"><span className="font-medium text-stone-700">Decisions:</span> {e.decisions}</p>}
    </div>
  );
}

export default function QualityPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("trends");
  const [trends, setTrends] = useState(null);
  const [evvAgency, setEvvAgency] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [caps, setCaps] = useState(null);
  const [capsSummary, setCapsSummary] = useState(null);
  const [qm, setQm] = useState(null);
  const [qmSel, setQmSel] = useState(null);
  const [qmForm, setQmForm] = useState({ kind: "quarterly", quarter: "", year: new Date().getFullYear(), week_of: "", label: "", meeting_date: "" });

  const loadQm = () => api.get("/qm-meetings").then((r) => setQm(r.data)).catch((e) => toast.error(formatApiError(e)));

  const createQm = async () => {
    if (qmForm.kind === "quarterly" && !qmForm.quarter) { toast.error("Pick the quarter"); return; }
    if (qmForm.kind === "other" && !(qmForm.label || "").trim()) { toast.error("Give the meeting a label"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/qm-meetings", qmForm);
      toast.success(`Meeting created${data.auto_filled?.length ? ` — auto-filled: ${data.auto_filled.join(", ")}` : ""}`);
      setQmSel(data.meeting);
      loadQm();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const pullWeeklyReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/reports/weekly-compliance?week_of=${qmSel.week_start || qmSel.meeting_date || ""}`);
      const cur = (qmSel.notes || {}).weekly_report_issues || "";
      if (cur.trim() && !window.confirm("Replace the current weekly-report note with the pulled report?")) { setBusy(false); return; }
      setQmSel((s) => ({ ...s, notes: { ...(s.notes || {}), weekly_report_issues: data.text } }));
      toast.success(`Pulled report for ${data.week_start} → ${data.week_end} — remember to Save`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const delQm = async (m) => {
    if (!window.confirm(`Delete "${m.label || `${m.quarter} ${m.year}`}"? This cannot be undone.`)) return;
    try { await api.delete(`/qm-meetings/${m.id}`); toast.success("Meeting deleted"); loadQm(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveQm = async () => {
    setBusy(true);
    try {
      await api.put(`/qm-meetings/${qmSel.id}`, {
        meeting_date: qmSel.meeting_date, participants: qmSel.participants, status: qmSel.status,
        label: qmSel.label, measures: qmSel.measures, policy_review: qmSel.policy_review, notes: qmSel.notes,
      });
      toast.success("Meeting saved");
      loadQm();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const qmPatch = (section, key, field, value) =>
    setQmSel((s) => ({ ...s, [section]: { ...(s[section] || {}), [key]: { ...(s[section]?.[key] || {}), [field]: value } } }));

  const qmCharts = useMemo(() => {
    const num = (s, unit) => {
      let t = String(s || "");
      if (unit) t = t.split(unit)[0];
      const x = parseFloat(t.replace(/,/g, ""));
      return Number.isFinite(x) ? x : null;
    };
    return ((qm?.meetings) || [])
      .filter((m) => (m.kind || "quarterly") === "quarterly")
      .sort((a, b) => String(a.sort_key).localeCompare(String(b.sort_key)))
      .map((m) => {
        const ms = m.measures || {};
        const missedM = String(ms.visits?.value || "").match(/\/\s*([\d,]+)\s*missed/);
        return {
          q: `${m.quarter} ${String(m.year).slice(2)}`,
          evv: num(ms.evv?.value),
          visits: num(ms.visits?.value, " completed"),
          missed: missedM ? num(missedM[1]) : null,
          hours: num(ms.billable_hours?.value, " hrs"),
          incidents: num(ms.incidents?.value),
          complaints: num(ms.complaints?.value),
        };
      });
  }, [qm]);

  const [evvFile, setEvvFile] = useState(null);
  const [evvMonth, setEvvMonth] = useState("");
  const loadEvvAgency = () => api.get("/evv/agency").then((r) => setEvvAgency(r.data)).catch(() => {});
  const uploadEvv = async () => {
    if (!evvFile) { toast.error("Choose the EVV report file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", evvFile);
      const { data } = await api.post(`/evv/upload${evvMonth ? `?month=${evvMonth}` : ""}`, fd);
      toast.success(`EVV report uploaded — ${data.payer_rows ?? 0} payer rows, ${data.matched ?? 0} caregiver rows`);
      setEvvFile(null);
      loadEvvAgency();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const [showCapForm, setShowCapForm] = useState(false);
  const [capForm, setCapForm] = useState({});

  const [incidents, setIncidents] = useState(null);
  const [incForm, setIncForm] = useState({ kind: "incident", category: "", date: "", time: "", client_name: "", employee_involved: "", parties_involved: "", description: "", immediate_response: "", follow_up: "" });
  const [showIncForm, setShowIncForm] = useState(false);

  const loadIncidents = () => api.get("/incident-reports").then((r) => setIncidents(r.data.reports || [])).catch((e) => toast.error(formatApiError(e)));

  const [registers, setRegisters] = useState(null);
  const [vitals, setVitals] = useState([]);
  const loadRegisters = () => {
    Promise.all([api.get("/admin/incident-log"), api.get("/admin/complaints")])
      .then(([i, c]) => {
        const comps = (c.data.complaints || []).filter((x) => x.status !== "void");
        setRegisters({
          incidents: { total: i.data.total, open: i.data.open, recent: (i.data.incidents || []).slice(0, 3) },
          complaints: { total: comps.length,
                        open: comps.filter((x) => x.status !== "resolved").length,
                        recent: comps.slice(0, 3) },
        });
      }).catch(() => {});
  };

  const updIncident = async (id, patch) => {
    try { await api.put(`/incident-reports/${id}`, patch); loadIncidents(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const incidentCap = async (id) => {
    try {
      await api.post(`/incident-reports/${id}/cap`);
      toast.success("CAP created and linked — see Corrective Action Plans");
      loadIncidents();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitIncident = async () => {
    if (!incForm.category || !incForm.date || !(incForm.description || "").trim()) {
      toast.error("Category, date, and description are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/incident-reports", incForm);
      toast.success(data.serious ? "Filed — SERIOUS: office notified by email, policy deadlines tracked" : "Report filed");
      setIncForm({ kind: "incident", category: "", date: "", time: "", client_name: "", employee_involved: "", parties_involved: "", description: "", immediate_response: "", follow_up: "" });
      setShowIncForm(false);
      loadIncidents();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const loadCaps = () => api.get("/caps").then((r) => { setCaps(r.data.caps); setCapsSummary(r.data.summary); }).catch((e) => toast.error(formatApiError(e)));

  const submitCap = async () => {
    const steps = (capForm.stepsText || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [text, owner = "", due = ""] = l.split("|").map((p) => p.trim());
      return { text, owner, due_date: due };
    });
    if (!(capForm.title || "").trim() || !steps.length) {
      toast.error("Add a title and at least one action step");
      return;
    }
    setBusy(true);
    try {
      await api.post("/caps", { title: capForm.title, source: capForm.source || "", survey_date: capForm.survey_date || "", finding: capForm.finding || "", steps });
      toast.success("Corrective action plan saved");
      setShowCapForm(false);
      setCapForm({});
      loadCaps();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const setStepStatus = async (cap, step, patch) => {
    try {
      await api.put(`/caps/${cap.id}/steps/${step.id}`, { status: step.status, ...patch });
      loadCaps();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delCap = async (cap) => {
    if (!window.confirm(`Delete plan "${cap.title}"?`)) return;
    try {
      await api.delete(`/caps/${cap.id}`);
      toast.success("Deleted");
      loadCaps();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const loadTrends = () => {
    api.get("/qi/trends").then((r) => setTrends(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/evv/agency").then((r) => setEvvAgency(r.data)).catch(() => {});
    api.get("/cqi/quarterly-vitals").then((r) => setVitals(r.data.quarters || [])).catch(() => {});
  };
  const loadEntries = (k) => api.get(`/qi/entries?kind=${k}`).then((r) => setEntries(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "trends") loadTrends();
    else if (tab === "caps") loadCaps();
    else if (tab === "qm") loadQm();
    else if (tab === "incident_reports") { loadIncidents(); loadRegisters(); }
    else loadEntries(tab);
  }, [isAdmin, tab]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/qi/entries", { ...form, kind: tab });
      toast.success("Saved");
      setShowForm(false);
      setForm({});
      loadEntries(tab);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const setStatus = async (e, status) => {
    try {
      await api.put(`/qi/entries/${e.id}`, { status });
      loadEntries(tab);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try {
      await api.delete(`/qi/entries/${e.id}`);
      toast.success("Deleted");
      loadEntries(tab);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const maxMonth = useMemo(() => Math.max(1, ...(trends?.by_month || []).map((m) => m.count)), [trends]);

  if (!isAdmin) {
    return <div className="py-16 text-center text-sm text-stone-500" data-testid="qi-admin-only">Quality Improvement is available to admins only.</div>;
  }

  const F = (key, label, props = {}) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-stone-500">{label}</label>
      <Input value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="h-9 border-stone-300 bg-white text-sm" data-testid={`qi-form-${key}`} {...props} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="quality-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="qi-title">Continuous Quality Improvement</h1>
        <p className="mt-1 text-sm text-stone-500">Incident and complaint trends, audits, improvement plans, and quarterly committee reviews.</p>
        <a href={`${api.defaults.baseURL}/qm-agenda`} target="_blank" rel="noreferrer"
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="qi-agenda-link">
          Quarterly QM meeting agenda (PDF)
        </a>
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm" data-testid="qi-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setShowForm(false); }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-accent"}`}
            data-testid={`qi-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "trends" && (
        trends === null ? (
          <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
        ) : (
          <div className="space-y-4" data-testid="qi-trends">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Total reports", trends.total, "qi-trend-total"],
                ["Open / under review", trends.open, "qi-trend-open"],
                ["Incidents", trends.by_kind?.incident || 0, "qi-trend-incidents"],
                ["Complaints", trends.by_kind?.complaint || 0, "qi-trend-complaints"],
              ].map(([label, val, testid]) => (
                <div key={testid} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={testid}>
                  <p className="font-heading text-2xl font-bold text-stone-900">{val}</p>
                  <p className="text-xs text-stone-500">{label}</p>
                </div>
              ))}
            </div>
            {vitals.length > 0 && (
              <div className="grid gap-4 lg:grid-cols-2" data-testid="qi-vitals-charts">
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-chart-hours">
                  <h2 className="font-heading text-lg font-semibold text-stone-900">Billable hours by quarter</h2>
                  <p className="text-xs text-stone-400">From the visit log — matches the quarterly meeting forms</p>
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={vitals} margin={{ top: 12, right: 12, bottom: 0, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                      <Tooltip formatter={(v) => `${Number(v).toLocaleString()} hrs`} />
                      <Bar dataKey="hours" name="Billable hours" fill="#B7791F" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-chart-visits">
                  <h2 className="font-heading text-lg font-semibold text-stone-900">Visits by quarter — completed vs missed</h2>
                  <p className="text-xs text-stone-400">Missed from the EVV summary reports (quarters with a report on file)</p>
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={vitals} margin={{ top: 12, right: 12, bottom: 0, left: -12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                      <Tooltip />
                      <Bar dataKey="completed" name="Completed visits" fill="#2F6B4F" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="missed" name="Missed visits" fill="#C0392B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {evvAgency.length > 0 && (
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-evv-payers">
                <h2 className="font-heading text-lg font-semibold text-stone-900">EVV compliance by payer</h2>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-500">
                        <th className="py-2 pr-4 font-medium">Payer</th>
                        <th className="py-2 pr-4 font-medium">Period</th>
                        <th className="py-2 pr-4 font-medium">Month</th>
                        <th className="py-2 pr-4 font-medium">YTD</th>
                        <th className="py-2 font-medium">Confirmed visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evvAgency.map((r, i) => (
                        <tr key={`${r.payer}-${r.period}`} className="border-b border-stone-100 last:border-0" data-testid={`qi-evv-payer-${i}`}>
                          <td className="py-2 pr-4 text-stone-700">{r.payer}</td>
                          <td className="py-2 pr-4 text-stone-500">{r.period}</td>
                          <td className={`py-2 pr-4 font-medium ${(r.pct ?? 100) < 85 ? "text-[#C0392B]" : "text-[#1e8449]"}`} data-testid={`qi-evv-payer-pct-${i}`}>{r.pct ?? 0}%</td>
                          <td className={`py-2 pr-4 font-medium ${(r.ytd_pct ?? 100) < 85 ? "text-[#C0392B]" : "text-[#1e8449]"}`}>{r.ytd_pct ?? 0}%</td>
                          <td className="py-2 text-stone-600">{r.confirmed ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="evv-upload-card">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Monthly EVV report</h2>
              <p className="mt-1 text-xs text-stone-500">
                HHAeXchange emails the EVV Compliance Summary on the 1st of each month. Upload it here — it fills the
                quarterly meeting forms automatically (private pay is excluded from the numbers).
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input type="month" value={evvMonth} onChange={(e) => setEvvMonth(e.target.value)} className="h-9 w-44 border-stone-300 bg-white text-sm" data-testid="evv-upload-month" />
                <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setEvvFile(e.target.files?.[0] || null)} className="text-xs text-stone-600" data-testid="evv-upload-file" />
                <Button size="sm" onClick={uploadEvv} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="evv-upload-submit">
                  {busy ? "Uploading…" : "Upload report"}
                </Button>
              </div>
            </div>
            {trends.total === 0 ? (
              <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="qi-trends-empty">
                No incidents or complaints reported yet — trends appear here as they're logged on client records.
              </p>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-trend-by-month">
                  <h2 className="font-heading text-lg font-semibold text-stone-900">Reports by month</h2>
                  <div className="mt-3 space-y-1.5">
                    {trends.by_month.map((m, i) => (
                      <div key={m.month} className="flex items-center gap-2 text-sm" data-testid={`qi-month-${i}`}>
                        <span className="w-16 text-xs text-stone-500">{m.month}</span>
                        <div className="h-4 rounded-sm bg-primary/80" style={{ width: `${(m.count / maxMonth) * 100}%`, minWidth: 4 }} />
                        <span className="text-xs font-medium text-stone-700">{m.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-trend-by-category">
                  <h2 className="font-heading text-lg font-semibold text-stone-900">Reports by category</h2>
                  <div className="mt-2 divide-y divide-stone-100">
                    {trends.by_category.map((c, i) => (
                      <div key={c.category} className="flex items-center justify-between py-1.5 text-sm" data-testid={`qi-category-${i}`}>
                        <span className="text-stone-700">{c.category}</span>
                        <span className="font-medium text-stone-900">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {tab === "qm" && (
        <div className="space-y-4" data-testid="qm-section">
          {qm === null ? (
            <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
          ) : qmSel ? (
            <div className="space-y-4" data-testid="qm-form">
              <button onClick={() => setQmSel(null)} className="text-sm font-medium text-primary hover:underline" data-testid="qm-back">← All meetings</button>
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading text-lg font-semibold text-stone-900" data-testid="qm-form-title">{qmSel.label || `${qmSel.quarter} ${qmSel.year}`}</p>
                    <p className="text-xs text-stone-400">{(qmSel.kind || "quarterly") === "quarterly"
                      ? "Agenda per the SHC Quarterly QM Meeting Agenda (first week of Jan/Apr/Jul/Oct)"
                      : qmSel.kind === "weekly"
                        ? `Weekly New Hire & Employee Compliance — week of ${qmSel.week_start || "—"}`
                        : "General quality/compliance meeting record"}</p>
                  </div>
                  <span className="flex items-center gap-2">
                    <button onClick={() => setQmSel((s) => ({ ...s, status: s.status === "completed" ? "draft" : "completed" }))}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${qmSel.status === "completed" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-stone-300 bg-white text-stone-500"}`}
                      data-testid="qm-status-toggle">{qmSel.status === "completed" ? "Completed ✓" : "Draft — mark completed"}</button>
                    <a href={`${api.defaults.baseURL}/qm-meetings/${qmSel.id}/export`} target="_blank" rel="noreferrer"
                      className="rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-900 hover:text-white"
                      data-testid="qm-export">Meeting record (PDF)</a>
                    <a href={`${api.defaults.baseURL}/qm-meetings/${qmSel.id}/export-fillable.pdf`} target="_blank" rel="noreferrer"
                      className="rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-900 hover:text-white"
                      data-testid="qm-export-fillable">Fillable PDF (type on screen)</a>
                  </span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Meeting date</label>
                    <Input type="date" value={qmSel.meeting_date || ""} onChange={(e) => setQmSel((s) => ({ ...s, meeting_date: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="qm-date" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Participants (comma-separated)</label>
                    <Input value={(qmSel.participants || []).join(", ")} onChange={(e) => setQmSel((s) => ({ ...s, participants: e.target.value.split(",").map((p) => p.trim()).filter(Boolean) }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="qm-participants" />
                  </div>
                </div>
              </div>

              {(qmSel.kind || "quarterly") === "quarterly" && (<>
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <p className="font-heading text-sm font-semibold text-stone-900">Performance measures</p>
                <p className="mt-0.5 text-xs text-stone-400">Values auto-filled from system data where available. Declined or missed target? Flag QMP yes and assign leadership + deadline.</p>
                <div className="mt-3 space-y-3">
                  {QM_MEASURE_DEFS.map(([key, label]) => {
                    const v = (qmSel.measures || {})[key] || {};
                    return (
                      <div key={key} className="rounded-md border border-stone-100 bg-stone-50 p-3" data-testid={`qm-measure-${key}`}>
                        <p className="text-xs font-semibold text-stone-700">{label}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Input value={v.value || ""} onChange={(e) => qmPatch("measures", key, "value", e.target.value)} placeholder="This quarter" className="h-8 w-32 border-stone-300 bg-white text-xs" data-testid={`qm-${key}-value`} />
                          <Input value={v.target || ""} onChange={(e) => qmPatch("measures", key, "target", e.target.value)} placeholder="Target" className="h-8 w-24 border-stone-300 bg-white text-xs" data-testid={`qm-${key}-target`} />
                          {v.last_quarter && <span className="text-[10px] text-stone-400">last quarter: {v.last_quarter}</span>}
                          <span className="inline-flex gap-1">
                            {QM_STATUS_OPTS.map(([sv, sl]) => (
                              <button key={sv} onClick={() => qmPatch("measures", key, "status", v.status === sv ? "" : sv)}
                                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${v.status === sv ? (sv === "improved" ? "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" : sv === "declined" ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : "border-stone-400 bg-stone-200 text-stone-700") : "border-stone-200 bg-white text-stone-400 hover:border-stone-400"}`}
                                data-testid={`qm-${key}-status-${sv}`}>{sl}</button>
                            ))}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-stone-500">
                            QMP:
                            {[["yes", "Yes"], ["no", "No"]].map(([qv, ql]) => (
                              <button key={qv} onClick={() => qmPatch("measures", key, "qmp", v.qmp === qv ? "" : qv)}
                                className={`rounded-full border px-2 py-0.5 ${v.qmp === qv ? (qv === "yes" ? "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]" : "border-stone-400 bg-stone-200 text-stone-700") : "border-stone-200 bg-white text-stone-400 hover:border-stone-400"}`}
                                data-testid={`qm-${key}-qmp-${qv}`}>{ql}</button>
                            ))}
                          </span>
                        </div>
                        {v.qmp === "yes" && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Input value={v.leadership || ""} onChange={(e) => qmPatch("measures", key, "leadership", e.target.value)} placeholder="Assigned leadership" className="h-8 w-44 border-stone-300 bg-white text-xs" data-testid={`qm-${key}-leadership`} />
                            <Input type="date" value={v.deadline || ""} onChange={(e) => qmPatch("measures", key, "deadline", e.target.value)} className="h-8 w-36 border-stone-300 bg-white text-xs" data-testid={`qm-${key}-deadline`} />
                          </div>
                        )}
                        <Input value={v.note || ""} onChange={(e) => qmPatch("measures", key, "note", e.target.value)} placeholder="Note" className="mt-2 h-8 border-stone-300 bg-white text-xs" data-testid={`qm-${key}-note`} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <p className="font-heading text-sm font-semibold text-stone-900">Policies & procedures review</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {QM_POLICIES.map((name) => {
                    const cur = (qmSel.policy_review || {})[name] || "";
                    return (
                      <div key={name} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 bg-stone-50 px-3 py-2" data-testid={`qm-policy-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                        <span className="text-xs font-medium text-stone-700">{name}</span>
                        <span className="inline-flex gap-1">
                          {QM_REVIEW_OPTS.map(([rv, rl]) => (
                            <button key={rv} onClick={() => qmPatch("policy_review", name, null, rv === cur ? "" : rv)}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cur === rv ? (rv === "updated" ? "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" : "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]") : "border-stone-200 bg-white text-stone-400 hover:border-stone-400"}`}
                              data-testid={`qm-policy-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${rv}`}>{rl}</button>
                          ))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              </>)}

              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-heading text-sm font-semibold text-stone-900">Discussion notes</p>
                  <button onClick={pullWeeklyReport} disabled={busy}
                    className="rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-900 hover:text-white disabled:opacity-50"
                    data-testid="qm-pull-weekly">Pull weekly compliance report</button>
                </div>
                {QM_NOTES.map(([k, label]) => (
                  <div key={k} className="mb-3 last:mb-0">
                    <label className="mb-1 block text-xs font-medium text-stone-500">{label}</label>
                    <Textarea value={(qmSel.notes || {})[k] || ""} onChange={(e) => setQmSel((s) => ({ ...s, notes: { ...(s.notes || {}), [k]: e.target.value } }))} className="min-h-16 border-stone-300 bg-white text-sm" data-testid={`qm-note-${k}`} />
                  </div>
                ))}
              </div>

              <Button onClick={saveQm} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="qm-save">
                {busy ? "Saving…" : "Save meeting"}
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-stone-800">New meeting</p>
                <p className="mt-0.5 text-xs text-stone-400">Quarterly QM meetings follow the agenda form (first week of Jan/Apr/Jul/Oct). Weekly meetings auto-pull the Weekly New Hire & Employee Compliance Report.</p>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Meeting type</label>
                    <Select value={qmForm.kind} onValueChange={(v) => setQmForm((f) => ({ ...f, kind: v }))}>
                      <SelectTrigger className="h-9 w-52 border-stone-300 bg-white text-sm" data-testid="qm-new-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quarterly">Quarterly QM meeting</SelectItem>
                        <SelectItem value="weekly">Weekly compliance meeting</SelectItem>
                        <SelectItem value="other">Other meeting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {qmForm.kind === "quarterly" && (
                    <>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-stone-500">Quarter</label>
                        <Select value={qmForm.quarter} onValueChange={(v) => setQmForm((f) => ({ ...f, quarter: v }))}>
                          <SelectTrigger className="h-9 w-28 border-stone-300 bg-white text-sm" data-testid="qm-new-quarter"><SelectValue placeholder="Q#" /></SelectTrigger>
                          <SelectContent>{["Q1", "Q2", "Q3", "Q4"].map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-stone-500">Year</label>
                        <Input type="number" value={qmForm.year} onChange={(e) => setQmForm((f) => ({ ...f, year: e.target.value }))} className="h-9 w-24 border-stone-300 bg-white text-sm" data-testid="qm-new-year" />
                      </div>
                    </>
                  )}
                  {qmForm.kind === "weekly" && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-stone-500">Any day in that week</label>
                      <Input type="date" value={qmForm.week_of} onChange={(e) => setQmForm((f) => ({ ...f, week_of: e.target.value }))} className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="qm-new-week" />
                    </div>
                  )}
                  {qmForm.kind === "other" && (
                    <>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-stone-500">Meeting label</label>
                        <Input value={qmForm.label} onChange={(e) => setQmForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Safety committee" className="h-9 w-52 border-stone-300 bg-white text-sm" data-testid="qm-new-label" />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-stone-500">Date</label>
                        <Input type="date" value={qmForm.meeting_date} onChange={(e) => setQmForm((f) => ({ ...f, meeting_date: e.target.value }))} className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="qm-new-date" />
                      </div>
                    </>
                  )}
                  <Button onClick={createQm} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="qm-create">
                    {busy ? "Creating…" : "Start meeting form"}
                  </Button>
                </div>
              </div>

              {qmCharts.some((d) => d.evv !== null || d.visits !== null || d.hours !== null) && (
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qm-charts">
                  <p className="font-heading text-sm font-semibold text-stone-900">Trends across quarters</p>
                  <p className="mt-0.5 text-xs text-stone-400">From your meeting forms and the visit log — private pay excluded</p>
                  <div className="mt-4 grid gap-5 lg:grid-cols-2">
                    <div data-testid="qm-chart-evv">
                      <p className="mb-1 text-xs font-semibold text-stone-600">EVV compliance %</p>
                      <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={qmCharts} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                          <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                          <YAxis domain={[80, 100]} tick={{ fontSize: 11, fill: "#78716c" }} />
                          <Tooltip formatter={(v) => (v === null ? "no data" : `${v}%`)} />
                          <ReferenceLine y={90} stroke="#B7791F" strokeDasharray="4 4" label={{ value: "90%", fontSize: 10, fill: "#B7791F" }} />
                          <Line type="monotone" dataKey="evv" name="EVV compliance" stroke="#4A2340" strokeWidth={2.5} dot={{ r: 4, fill: "#4A2340" }} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div data-testid="qm-chart-visits">
                      <p className="mb-1 text-xs font-semibold text-stone-600">Completed visits (missed in red)</p>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={qmCharts} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                          <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                          <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                          <Tooltip />
                          <Bar dataKey="visits" name="Completed visits" fill="#2F6B4F" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="missed" name="Missed visits" fill="#C0392B" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div data-testid="qm-chart-hours">
                      <p className="mb-1 text-xs font-semibold text-stone-600">Total billable hours</p>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={qmCharts} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                          <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                          <YAxis tick={{ fontSize: 11, fill: "#78716c" }} />
                          <Tooltip formatter={(v) => (v === null ? "no data" : `${Number(v).toLocaleString()} hrs`)} />
                          <Bar dataKey="hours" name="Billable hours" fill="#B7791F" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div data-testid="qm-chart-reports">
                      <p className="mb-1 text-xs font-semibold text-stone-600">Incidents & complaints</p>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={qmCharts} margin={{ top: 8, right: 12, bottom: 0, left: -22 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                          <XAxis dataKey="q" tick={{ fontSize: 11, fill: "#78716c" }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#78716c" }} />
                          <Tooltip />
                          <Bar dataKey="incidents" name="Incidents" fill="#4A2340" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="complaints" name="Complaints" fill="#B7791F" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {qm.meetings.length === 0 ? (
                <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="qm-empty">No QM meetings recorded yet — start this quarter's above.</p>
              ) : (
                <div className="space-y-5" data-testid="qm-list">
                  {[
                    ["Quarterly QM meetings", qm.meetings.filter((x) => (x.kind || "quarterly") === "quarterly")],
                    ["Weekly & other meetings", qm.meetings.filter((x) => (x.kind || "quarterly") !== "quarterly")],
                  ].map(([group, list]) => list.length === 0 ? null : (
                    <div key={group}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400" data-testid={`qm-group-${group.includes("Quarterly") ? "quarterly" : "other"}`}>{group}</p>
                      <div className="space-y-3">
                        {list.map((m) => (
                          <button key={m.id} onClick={() => setQmSel(m)} className="w-full rounded-lg border border-stone-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-primary/40" data-testid={`qm-row-${m.id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="font-medium text-stone-900">{m.label || `${m.quarter} ${m.year}`} <span className="text-xs font-normal text-stone-400">· meeting {m.meeting_date || "unscheduled"} · {(m.participants || []).length} participants</span></p>
                              <span className="flex items-center gap-2 text-xs">
                                <span className={m.status === "completed" ? "font-semibold text-[#1e8449]" : "text-stone-400"}>{m.status === "completed" ? "Completed" : "Draft"}</span>
                                {m.measures_declined > 0 && <span className="rounded-full bg-[#C0392B]/10 px-2 py-0.5 font-semibold text-[#C0392B]">{m.measures_declined} declined</span>}
                                {m.qmps_required > 0 && <span className="rounded-full bg-[#B7791F]/10 px-2 py-0.5 font-semibold text-[#8a5d14]">{m.qmps_required} QMPs</span>}
                                <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); delQm(m); }} className="ml-1 text-stone-300 hover:text-[#C0392B]" data-testid={`qm-delete-${m.id}`} title="Delete meeting"><Trash size={14} /></span>
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "caps" && (
        <div className="space-y-4" data-testid="caps-section">
          {capsSummary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Plans", capsSummary.total, "caps-total"], ["Fully verified", capsSummary.verified, "caps-verified"],
                ["In progress", capsSummary.in_progress, "caps-in-progress"], ["With overdue steps", capsSummary.overdue, "caps-overdue"]].map(([label, val, tid]) => (
                <div key={tid} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={tid}>
                  <p className="font-heading text-2xl font-bold text-stone-900">{val}</p>
                  <p className="text-xs text-stone-500">{label}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={() => setShowCapForm((v) => !v)} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="cap-add-button">
              <Plus size={15} className="mr-1.5" /> {showCapForm ? "Cancel" : "New corrective action plan"}
            </Button>
          </div>
          {showCapForm && (
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="cap-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Plan title</label>
                  <Input value={capForm.title || ""} onChange={(e) => setCapForm((f) => ({ ...f, title: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="cap-form-title" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Source (DOH survey, UPMC finding, internal audit…)</label>
                  <Input value={capForm.source || ""} onChange={(e) => setCapForm((f) => ({ ...f, source: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="cap-form-source" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Survey / finding date</label>
                  <Input type="date" value={capForm.survey_date || ""} onChange={(e) => setCapForm((f) => ({ ...f, survey_date: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="cap-form-date" />
                </div>
              </div>
              <div className="mt-3 space-y-3">
                <Textarea value={capForm.finding || ""} onChange={(e) => setCapForm((f) => ({ ...f, finding: e.target.value }))} placeholder="What the surveyor found (the deficiency)" className="border-stone-300 bg-white text-sm" data-testid="cap-form-finding" />
                <div>
                  <Textarea value={capForm.stepsText || ""} onChange={(e) => setCapForm((f) => ({ ...f, stepsText: e.target.value }))}
                    placeholder={"Action steps — one per line:\nUpdate abuse policy to include X | Kelly | 2026-09-01\nRetrain all staff on updated policy | Kelly | 2026-09-15"} className="min-h-24 border-stone-300 bg-white font-mono text-sm" data-testid="cap-form-steps" />
                  <p className="mt-1 text-xs text-stone-400">One step per line: what we'll do | owner | due date (YYYY-MM-DD)</p>
                </div>
              </div>
              <Button onClick={submitCap} disabled={busy} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="cap-save-button">
                {busy ? "Saving…" : "Save plan"}
              </Button>
            </div>
          )}
          {caps === null ? (
            <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
          ) : caps.length === 0 ? (
            <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="caps-empty">No corrective action plans yet — add one from your latest survey findings above.</p>
          ) : (
            <div className="space-y-3" data-testid="caps-list">
              {caps.map((c) => (
                <div key={c.id} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`cap-${c.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-stone-900">{c.title}</p>
                      <p className="mt-0.5 text-xs text-stone-400">{c.source || "no source"}{c.survey_date ? ` · ${c.survey_date}` : ""}</p>
                    </div>
                    <span className="flex items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.health === "verified" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : c.health === "overdue" ? "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"}`} data-testid={`cap-health-${c.id}`}>
                        {c.health === "verified" ? "Verified complete" : c.health === "overdue" ? `${c.steps_overdue} overdue` : "In progress"}
                      </span>
                      <span className="text-xs text-stone-500" data-testid={`cap-progress-${c.id}`}>{c.steps_verified}/{c.steps_total} verified</span>
                      <a href={`${api.defaults.baseURL}/caps/${c.id}/export`} target="_blank" rel="noreferrer"
                        className="text-[10px] font-semibold text-primary underline" data-testid={`cap-export-${c.id}`}>Packet PDF</a>
                      <button onClick={() => delCap(c)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`cap-delete-${c.id}`} title="Delete"><Trash size={14} /></button>
                    </span>
                  </div>
                  {c.finding && <p className="mt-2 text-sm text-stone-600"><span className="font-medium text-stone-700">Finding:</span> {c.finding}</p>}
                  <div className="mt-3 space-y-2">
                    {(c.steps || []).map((s) => {
                      const overdue = s.due_date && s.due_date < new Date().toISOString().slice(0, 10) && !["done", "verified"].includes(s.status);
                      return (
                        <div key={s.id} className="rounded-md border border-stone-100 bg-stone-50 p-2.5" data-testid={`cap-step-${s.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Select value={s.status || "not_started"} onValueChange={(v) => setStepStatus(c, s, { status: v })}>
                              <SelectTrigger className={`h-7 w-32 border text-xs font-medium ${s.status === "verified" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : s.status === "done" ? "border-primary/30 bg-accent text-accent-foreground" : s.status === "in_progress" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" : "border-stone-300 bg-white text-stone-500"}`} data-testid={`cap-step-status-${s.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[["not_started", "Not started"], ["in_progress", "In progress"], ["done", "Done"], ["verified", "Verified — we did it"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <span className="flex-1 text-sm text-stone-800">{s.text}</span>
                            <span className="text-xs text-stone-400">{s.owner || "—"}</span>
                            {s.due_date && <span className={`text-xs ${overdue ? "font-semibold text-[#C0392B]" : "text-stone-400"}`}>due {s.due_date}{overdue ? " — overdue" : ""}</span>}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2">
                            <Input defaultValue={s.evidence || ""} placeholder="Evidence / proof we did it (e.g. policy v3 approved, training log 2026-09-10)"
                              onBlur={(e) => { if (e.target.value !== (s.evidence || "")) setStepStatus(c, s, { evidence: e.target.value }); }}
                              className="h-7 border-stone-200 bg-white text-xs" data-testid={`cap-step-evidence-${s.id}`} />
                          </div>
                          {s.status === "verified" && s.verified_by && (
                            <p className="mt-1 text-[10px] text-stone-400">Verified by {s.verified_by} · {String(s.verified_at).slice(0, 10)}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "incident_reports" && (
        <div className="space-y-4" data-testid="incident-reports-section">
          {registers && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="register-cards">
              <Link to="/incidents" className="block rounded-lg border border-stone-200 bg-white p-5 shadow-sm transition-colors hover:border-primary/40" data-testid="register-incidents-card">
                <p className="font-heading text-sm font-semibold text-stone-900">Incident register</p>
                <p className="mt-1 font-heading text-2xl font-bold text-stone-900" data-testid="register-incidents-total">{registers.incidents.total}</p>
                <p className="text-xs text-stone-500">{registers.incidents.open} open · full log & print →</p>
                <div className="mt-2 space-y-1">
                  {registers.incidents.recent.map((r, i) => (
                    <p key={i} className="truncate text-xs text-stone-500" data-testid={`register-incident-recent-${i}`}>
                      {r.date || "—"} · {r.client_name || ""} · {(r.category || "incident")}
                    </p>
                  ))}
                </div>
              </Link>
              <Link to="/complaints" className="block rounded-lg border border-stone-200 bg-white p-5 shadow-sm transition-colors hover:border-primary/40" data-testid="register-complaints-card">
                <p className="font-heading text-sm font-semibold text-stone-900">Complaint register</p>
                <p className="mt-1 font-heading text-2xl font-bold text-stone-900" data-testid="register-complaints-total">{registers.complaints.total}</p>
                <p className="text-xs text-stone-500">{registers.complaints.open} open · full log & print →</p>
                <div className="mt-2 space-y-1">
                  {registers.complaints.recent.map((r, i) => (
                    <p key={i} className="truncate text-xs text-stone-500" data-testid={`register-complaint-recent-${i}`}>
                      {r.received_date || "—"} · {r.client_name || ""} · {(r.summary || "").slice(0, 60)}
                    </p>
                  ))}
                </div>
              </Link>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="max-w-xl text-xs text-stone-400">Reports below are filed by staff (forms & wrap-ups). Critical Incident Policy: office review within 24h · EIM entry 48h (serious) · investigation 5 business days · CAP 30 days. Serious incidents email the office automatically. APS: suspected abuse/neglect/exploitation is reported immediately.</p>
            <Button onClick={() => setShowIncForm((v) => !v)} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="incident-add-button">
              <Plus size={15} className="mr-1.5" /> {showIncForm ? "Cancel" : "File incident / complaint"}
            </Button>
          </div>

          {showIncForm && (
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="incident-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Type</label>
                  <div className="flex gap-2">
                    {[["incident", "Incident"], ["complaint", "Complaint"]].map(([kv, kl]) => (
                      <button key={kv} onClick={() => setIncForm((f) => ({ ...f, kind: kv }))}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${incForm.kind === kv ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
                        data-testid={`incident-kind-${kv}`}>{kl}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Category *</label>
                  <select value={incForm.category} onChange={(e) => setIncForm((f) => ({ ...f, category: e.target.value }))}
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="incident-category">
                    <option value="">Select…</option>
                    {INCIDENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}{SERIOUS_CATEGORIES.includes(c) && incForm.kind === "incident" ? " (serious)" : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Date *</label>
                  <Input type="date" value={incForm.date} onChange={(e) => setIncForm((f) => ({ ...f, date: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="incident-date" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Time</label>
                  <Input type="time" value={incForm.time} onChange={(e) => setIncForm((f) => ({ ...f, time: e.target.value }))} className="h-9 border-stone-300 bg-white text-sm" data-testid="incident-time" />
                </div>
                <Input value={incForm.client_name} onChange={(e) => setIncForm((f) => ({ ...f, client_name: e.target.value }))} placeholder="Client involved (optional)" className="h-9 border-stone-300 bg-white text-sm" data-testid="incident-client" />
                <Input value={incForm.employee_involved} onChange={(e) => setIncForm((f) => ({ ...f, employee_involved: e.target.value }))} placeholder="Employee involved (optional)" className="h-9 border-stone-300 bg-white text-sm" data-testid="incident-employee" />
              </div>
              <Textarea value={incForm.description} onChange={(e) => setIncForm((f) => ({ ...f, description: e.target.value }))} placeholder="What happened? *" className="mt-3 min-h-20 border-stone-300 bg-white text-sm" data-testid="incident-description" />
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Textarea value={incForm.immediate_response} onChange={(e) => setIncForm((f) => ({ ...f, immediate_response: e.target.value }))} placeholder="Immediate response taken" className="min-h-16 border-stone-300 bg-white text-sm" data-testid="incident-response" />
                <Textarea value={incForm.follow_up} onChange={(e) => setIncForm((f) => ({ ...f, follow_up: e.target.value }))} placeholder="Follow-up actions" className="min-h-16 border-stone-300 bg-white text-sm" data-testid="incident-followup" />
              </div>
              <Button onClick={submitIncident} disabled={busy} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="incident-save">
                {busy ? "Filing…" : "File report"}
              </Button>
            </div>
          )}

          {incidents === null ? (
            <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
          ) : incidents.length === 0 ? (
            <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="incident-empty">No incident or complaint reports yet. Staff can file from their Forms page — only the office sees them.</p>
          ) : (
            <div className="space-y-3" data-testid="incident-list">
              {incidents.map((r) => (
                <div key={r.id} className={`rounded-lg border bg-white p-4 shadow-sm ${r.serious ? "border-[#C0392B]/30" : "border-stone-200"}`} data-testid={`incident-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-semibold text-stone-800">{r.category}</span>
                    <span className="text-stone-400">{r.kind} · {r.date}{r.time ? ` ${r.time}` : ""}</span>
                    {r.serious && <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`incident-serious-${r.id}`}>SERIOUS</span>}
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.status === "Resolved" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : r.status === "Under Review" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" : "border-stone-300 bg-stone-100 text-stone-500"}`}>{r.status}</span>
                    <span className="text-stone-400">by {r.reporter_name}{r.reporter_role === "staff" ? " (staff)" : ""}</span>
                  </div>
                  {(r.client_name || r.employee_involved) && (
                    <p className="mt-1 text-xs text-stone-500">{r.client_name && <>Client: <span className="font-medium text-stone-700">{r.client_name}</span></>}{r.client_name && r.employee_involved && " · "}{r.employee_involved && <>Employee: <span className="font-medium text-stone-700">{r.employee_involved}</span></>}</p>
                  )}
                  <p className="mt-1.5 text-sm text-stone-600">{r.description}</p>
                  {(r.immediate_response || r.follow_up) && (
                    <p className="mt-1 text-xs text-stone-500">{r.immediate_response && <><span className="font-medium">Response:</span> {r.immediate_response}</>}{r.immediate_response && r.follow_up && " · "}{r.follow_up && <><span className="font-medium">Follow-up:</span> {r.follow_up}</>}</p>
                  )}
                  {(r.flags || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`incident-flags-${r.id}`}>
                      {r.flags.map((fl) => (
                        <span key={fl} className="rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C0392B]">{fl}</span>
                      ))}
                    </div>
                  )}
                  {r.serious && (
                    <p className="mt-2 text-[10px] text-stone-400">Deadlines: review {r.review_due} · EIM {r.eim_due} · investigation {r.investigation_due} · CAP {r.cap_due}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3">
                    <Select value={r.status} onValueChange={(v) => updIncident(r.id, { status: v })}>
                      <SelectTrigger className="h-7 w-36 border-stone-300 text-xs" data-testid={`incident-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["Open", "Under Review", "Resolved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    {r.serious && (
                      <>
                        <button onClick={() => updIncident(r.id, { eim_entered: !r.eim_entered })}
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${r.eim_entered ? "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" : "border-stone-300 bg-white text-stone-500"}`}
                          data-testid={`incident-eim-${r.id}`}>{r.eim_entered ? "EIM entered ✓" : "Mark EIM entered"}</button>
                        <button onClick={() => updIncident(r.id, { aps_reported: !r.aps_reported })}
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${r.aps_reported ? "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]" : "border-stone-300 bg-white text-stone-500"}`}
                          data-testid={`incident-aps-${r.id}`}>{r.aps_reported ? "APS reported ✓" : "Mark APS reported"}</button>
                        {r.cap_id ? (
                          <span className="rounded-full border border-[#27AE60]/40 bg-[#27AE60]/10 px-2.5 py-1 text-[10px] font-semibold text-[#1e8449]" data-testid={`incident-cap-linked-${r.id}`}>CAP linked ✓</span>
                        ) : (
                          <button onClick={() => incidentCap(r.id)} className="rounded-full border border-[#C0392B]/40 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid={`incident-cap-${r.id}`}>Create CAP</button>
                        )}
                      </>
                    )}
                    <Input defaultValue={r.resolution || ""} placeholder="Resolution / outcome"
                      onBlur={(e) => { if (e.target.value !== (r.resolution || "")) updIncident(r.id, { resolution: e.target.value }); }}
                      className="h-7 min-w-48 flex-1 border-stone-200 bg-white text-xs" data-testid={`incident-resolution-${r.id}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!["trends", "caps", "qm", "incident_reports"].includes(tab) && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowForm((v) => !v)} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="qi-add-button">
              <Plus size={15} className="mr-1.5" /> {showForm ? "Cancel" : `Add ${TABS.find(([k]) => k === tab)?.[1].replace(/s$/, "")}`}
            </Button>
          </div>

          {showForm && tab === "committee_note" && (
            <CommitteeMeetingForm onSaved={() => { setShowForm(false); loadEntries(tab); }} onCancel={() => setShowForm(false)} />
          )}

          {showForm && tab !== "committee_note" && (
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="qi-form">
              <div className="grid gap-3 sm:grid-cols-2">
                {F("title", tab === "committee_note" ? "Meeting title" : "Title")}
                {F("date", "Date", { type: "date" })}
                {tab === "audit" && F("audit_type", "Audit type (chart audit, visit observation…)")}
                {tab === "audit" && F("scope", "Scope")}
                {tab === "improvement_plan" && F("source", "Source (audit, trend, complaint, survey…)")}
                {tab === "improvement_plan" && F("owner", "Owner")}
                {tab === "improvement_plan" && F("due_date", "Due date", { type: "date" })}
                {tab === "committee_note" && F("quarter", "Quarter (e.g. 2026-Q3)")}
                {tab === "committee_note" && F("attendees", "Attendees")}
              </div>
              <div className="mt-3 space-y-3">
                {tab === "audit" && <Textarea value={form.findings || ""} onChange={(e) => setForm((f) => ({ ...f, findings: e.target.value }))} placeholder="Findings" className="border-stone-300 bg-white text-sm" data-testid="qi-form-findings" />}
                {tab === "audit" && F("result", "Result / score")}
                {tab === "improvement_plan" && <Textarea value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What needs to improve and why" className="border-stone-300 bg-white text-sm" data-testid="qi-form-description" />}
                {tab === "improvement_plan" && <Textarea value={form.actions || ""} onChange={(e) => setForm((f) => ({ ...f, actions: e.target.value }))} placeholder="Corrective actions" className="border-stone-300 bg-white text-sm" data-testid="qi-form-actions" />}
                {tab === "committee_note" && <Textarea value={form.summary || ""} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} placeholder="Meeting summary" className="border-stone-300 bg-white text-sm" data-testid="qi-form-summary" />}
                {tab === "committee_note" && <Textarea value={form.decisions || ""} onChange={(e) => setForm((f) => ({ ...f, decisions: e.target.value }))} placeholder="Decisions / follow-ups" className="border-stone-300 bg-white text-sm" data-testid="qi-form-decisions" />}
              </div>
              <Button onClick={submit} disabled={busy || !(form.title || "").trim()} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="qi-save-button">
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          )}

          {entries.length === 0 ? (
            <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="qi-empty">Nothing here yet — add the first one above.</p>
          ) : (
            <div className="space-y-3" data-testid="qi-list">
              {entries.map((e) => e.kind === "committee_note"
                ? <CommitteeMeetingCard key={e.id} e={e} onDelete={del} />
                : <EntryCard key={e.id} e={e} onStatus={setStatus} onDelete={del} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
